import axios, { isAxiosError } from 'axios';
import { atom } from 'jotai';
import type { AccessErrorBody, AccessErrorCode, ImpersonationErrorBody } from '../../../shared/types/access';
import { store } from './atom.service';

/** The shared HTTP client for /api. Retries are React Query's job (see shouldRetry). */
export const api = axios.create({
    baseURL: window.origin,
    withCredentials: true,
});

/** Bumped (with a timestamp) whenever the server refused something only the user may do. */
export const notAllowedRefusalAtom = atom(0);

export function isNotAllowedRefusal(err: unknown): boolean {
    if (!isAxiosError(err) || err.response?.status !== 403) return false;
    return (err.response.data as Partial<ImpersonationErrorBody> | undefined)?.code === 'IMPERSONATION_NOT_ALLOWED';
}

/** Response interceptor: surface refusals while acting as someone as a notice (the banner shows it). */
export function noticeNotAllowedRefusal(err: unknown) {
    if (isNotAllowedRefusal(err)) store.set(notAllowedRefusalAtom, Date.now());
    return Promise.reject(err);
}

// While a Persera admin views as someone else, writes are refused: tell them why
api.interceptors.response.use(undefined, noticeNotAllowedRefusal);

/** Server `{ message }` from a failed request, or `fallback` when there is none (or it's empty). */
export function errorMessage(err: unknown, fallback: string): string {
    if (!isAxiosError(err)) return fallback;
    return (err.response?.data as { message?: string } | undefined)?.message || fallback;
}

const CODES: AccessErrorCode[] = ['NOT_FOUND', 'SIGN_IN_REQUIRED', 'FORBIDDEN', 'RESOURCE_FULL', 'PREMIUM_REQUIRED', 'WORKSPACE_ONLY'];

/** If `err` is an access refusal from the server, return it; otherwise null. */
export function toAccessError(err: unknown): AccessErrorBody | null {
    if (!isAxiosError(err)) return null;
    const body = err.response?.data as Partial<AccessErrorBody> | undefined;
    if (body?.code && CODES.includes(body.code)) {
        return { code: body.code, message: body.message ?? '' };
    }
    if (err.response?.status === 404) return { code: 'NOT_FOUND', message: '' };
    return null;
}

/** Don't retry access refusals: they won't change by retrying. */
export function shouldRetry(failureCount: number, err: unknown): boolean {
    if (toAccessError(err)) return false;
    return failureCount < 2;
}
